<script setup>
/**
 * CreateNewClientView
 * Multi-step wizard for agents to create new clients
 */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { collection, addDoc } from 'firebase/firestore'
import { getFirebaseDb } from '@/core/firebase/firebase'
import { useAuthStore } from '@/app/store/auth'
import { useToast } from '@/core/composables/useToast'
import { useStorage } from '@/core/composables/useStorage'
import { useEmail } from '@/core/composables/useEmail'
import { generateNewClientEmailHtml } from '@/core/utils/emailTemplates'
import { getEmailRecipients } from '@/core/services/emailSections'
import { ROUTES } from '@/core/config/constants'
import { fetchCities, fetchStreets } from '@/core/services/israelAddresses'

// Components
import FormField from '@/ui/components/common/FormField.vue'
import FormSelect from '@/ui/components/common/FormSelect.vue'
import FormStepper from '@/ui/components/common/FormStepper.vue'
import ImageUpload from '@/ui/components/common/ImageUpload.vue'
import SignaturePad from '@/ui/components/common/SignaturePad.vue'
import AppButton from '@/ui/components/AppButton.vue'

const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()
const { uploadImage } = useStorage()
const { sendEmail } = useEmail()

// Step management
const currentStep = ref(0)
const isSubmitting = ref(false)
const isMobile = ref(false)
const isTestMode = ref(false)

const steps = [
  'פרטי העסק',
  'פרטי בעל העסק',
  'פרטי עסק נוספים',
  'פרטי תשלום',
  'פירוט ביקורים',
  'סיום',
]

// Form data
const formData = ref({
  createdAt: null,
  // Step 1 - Business details
  client_name: '',
  business_id: '',
  city: '',
  street: '',
  house_number: '',
  address_comments: '',
  business_phone: '',
  mobile_phone: '',
  fax_number: '',
  contact_name: '',
  // Step 2 - Owner details
  owner_full_name: '',
  owner_id: '',
  owner_birth_date: '',
  owner_address: '',
  owner_id_photo: '',
  owner_chash_photo: '',
  owner_guaranty_photo: '',
  owner_business_front_photo: '',
  // Step 3 - Business type
  business_type: '',
  business_seniority: 0,
  back_check: '',
  kupot_qnt: 0,
  kosher_type: '',
  // Step 4 - Payment
  payment_type: '',
  bank_name: '',
  bank_branche_number: '',
  bank_account_number: '',
  bank_account_name: '',
  credit_days: '',
  check_delivery: '',
  // Step 5 - Visits
  visit_days: '',
  visit_frequency: '',
  visit_frequency_twice_a_week: '',
  visit_frequency_once_a_month: '',
  visit_type_manual: '',
  visit_like_customer: '',
  is_visit_limit: '',
  visit_limit_hour_start: '',
  visit_limit_hour_end: '',
  // Step 6 - Signature
  signature: '',
  agent_name: '',
  agent_number: '',
})

// Options for selects
const businessTypeOptions = [
  'מכולת/מעדנייה',
  'מינימרקט (קופה אחת)',
  'סופר מרקט (2 קופות ומעלה)',
  'פיצוצייה / דרגסטור',
  'אחר',
]

const yesNoOptions = ['כן', 'לא']

const kosherTypeOptions = ['לא כשר', 'כשר - יש משגיח', 'כשר - אין משגיח']

const paymentTypeOptions = ['מזומן', 'המחאה - שיק', 'העברה בנקאית']

const bankNameOptions = [
  'לאומי',
  'הפועלים',
  'דיסקונט',
  'מרכנתיל דיסקונט',
  'הבינלאומי',
  'מסד',
  'יהב',
  'הדואר',
  'ירושלים',
  'אחר',
]

const checkDeliveryOptions = ['סוכן', 'נהג']

const visitDaysOptions = ['א', 'ב', 'ג', 'ד', 'ה', 'ו']

const visitFrequencyOptions = ['פעם בשבוע', 'פעם בשבועיים', 'פעם בחודש', 'כמו לקוח']

const visitFrequencyTwiceOptions = ['1 פלוס 3', '2 פלוס 4', 'ידני']

const visitFrequencyOnceOptions = ['שבוע-1', 'שבוע-2', 'שבוע-3', 'שבוע-4', 'ידני']

// Computed
const agentName = computed(() => authStore.userDisplayName || '')
const agentNumber = computed(() => authStore.effectiveAgentNumber || '')

const showBankFields = computed(
  () => formData.value.payment_type && formData.value.payment_type !== 'מזומן'
)

const showTwiceWeekOption = computed(() => formData.value.visit_frequency === 'פעם בשבועיים')

const showOnceMonthOption = computed(() => formData.value.visit_frequency === 'פעם בחודש')

const showLikeCustomerField = computed(() => formData.value.visit_frequency === 'כמו לקוח')

const showVisitLimitHours = computed(() => formData.value.is_visit_limit === 'כן')

const uploadFolder = computed(
  () => `Agents/Clients/${agentName.value || 'unknown'}/${formData.value.client_name || 'temp'}`
)

const showManualVisitType = computed(
  () =>
    formData.value.visit_frequency_twice_a_week === 'ידני' ||
    formData.value.visit_frequency_once_a_month === 'ידני'
)

// ─── City / Street Government API ───
const cities = ref([])
const cityCodeMap = new Map()
const loadingCities = ref(true)

const getCities = async () => {
  try {
    const list = await fetchCities()
    list.forEach((c) => {
      if (c.code != null && !cityCodeMap.has(c.name)) {
        cityCodeMap.set(c.name, c.code)
      }
    })
    cities.value = list.map((c) => c.name)
  } catch (error) {
    console.error('Error fetching cities:', error)
    cities.value = []
  } finally {
    loadingCities.value = false
  }
}

const streets = ref([])
const loadingStreets = ref(false)

const getStreets = async (cityCode) => {
  loadingStreets.value = true
  streets.value = []
  try {
    streets.value = await fetchStreets(cityCode)
  } catch (error) {
    console.error('Error fetching streets:', error)
  } finally {
    loadingStreets.value = false
  }
}

// City autocomplete
const citySearch = ref('')
const showCityDropdown = ref(false)
const cityHighlightedIndex = ref(-1)

const filteredCities = computed(() => {
  if (!citySearch.value.trim()) return cities.value.slice(0, 50)
  const q = citySearch.value.trim()
  return cities.value.filter((c) => c.includes(q)).slice(0, 50)
})

const selectCity = (city) => {
  formData.value.city = city
  citySearch.value = city
  showCityDropdown.value = false
  cityHighlightedIndex.value = -1
}

const onCityInput = () => {
  showCityDropdown.value = true
  cityHighlightedIndex.value = -1
  if (formData.value.city && citySearch.value !== formData.value.city) {
    formData.value.city = ''
  }
}

const onCityKeydown = (e) => {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    cityHighlightedIndex.value = Math.min(
      cityHighlightedIndex.value + 1,
      filteredCities.value.length - 1
    )
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    cityHighlightedIndex.value = Math.max(cityHighlightedIndex.value - 1, 0)
  } else if (e.key === 'Enter' && cityHighlightedIndex.value >= 0) {
    e.preventDefault()
    selectCity(filteredCities.value[cityHighlightedIndex.value])
  } else if (e.key === 'Escape') {
    showCityDropdown.value = false
  }
}

const onCityBlur = () => {
  setTimeout(() => {
    showCityDropdown.value = false
  }, 200)
}

// Street autocomplete
const streetSearch = ref('')
const selectedStreet = ref(null)
const showStreetDropdown = ref(false)
const streetHighlightedIndex = ref(-1)

const filteredStreets = computed(() => {
  if (!streetSearch.value.trim()) return streets.value.slice(0, 50)
  const q = streetSearch.value.trim()
  return streets.value.filter((s) => s.includes(q)).slice(0, 50)
})

const selectStreet = (street) => {
  selectedStreet.value = street
  formData.value.street = street
  streetSearch.value = street
  showStreetDropdown.value = false
  streetHighlightedIndex.value = -1
}

const onStreetInput = () => {
  showStreetDropdown.value = true
  streetHighlightedIndex.value = -1
  if (selectedStreet.value && streetSearch.value !== selectedStreet.value) {
    selectedStreet.value = null
    formData.value.street = ''
  }
}

const onStreetKeydown = (e) => {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    streetHighlightedIndex.value = Math.min(
      streetHighlightedIndex.value + 1,
      filteredStreets.value.length - 1
    )
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    streetHighlightedIndex.value = Math.max(streetHighlightedIndex.value - 1, 0)
  } else if (e.key === 'Enter' && streetHighlightedIndex.value >= 0) {
    e.preventDefault()
    selectStreet(filteredStreets.value[streetHighlightedIndex.value])
  } else if (e.key === 'Escape') {
    showStreetDropdown.value = false
  }
}

const onStreetBlur = () => {
  setTimeout(() => {
    showStreetDropdown.value = false
  }, 200)
}

// Watch city changes to load streets
watch(
  () => formData.value.city,
  (newCity) => {
    if (newCity) {
      const code = cityCodeMap.get(newCity)
      if (code) getStreets(code)
    }
    streetSearch.value = ''
    selectedStreet.value = null
    formData.value.street = ''
    formData.value.house_number = ''
  }
)

// Signature pad ref
const signaturePadRef = ref(null)

// Test mode - fill all fields with test data
async function fillTestData() {
  return // Disable test data filling for now
  const testValue = 'TEST - בדיקה'

  formData.value = {
    createdAt: null,
    // Step 1 - Business details
    client_name: testValue,
    business_id: '123456789',
    city: testValue,
    street: testValue,
    house_number: '1',
    address_comments: testValue,
    business_phone: '0312345678',
    mobile_phone: '0501234567',
    fax_number: '0312345679',
    contact_name: testValue,
    // Step 2 - Owner details
    owner_full_name: testValue,
    owner_id: '123456789',
    owner_birth_date: '1990-01-01',
    owner_address: testValue,
    owner_id_photo: 'https://placehold.co/200x150/6366f1/white?text=TEST',
    owner_chash_photo: 'https://placehold.co/200x150/6366f1/white?text=TEST',
    owner_guaranty_photo: 'https://placehold.co/200x150/6366f1/white?text=TEST',
    owner_business_front_photo: 'https://placehold.co/200x150/6366f1/white?text=TEST',
    // Step 3 - Business type
    business_type: 'מכולת/מעדנייה',
    business_seniority: 5,
    back_check: 'לא',
    kupot_qnt: 2,
    kosher_type: 'כשר - יש משגיח',
    // Step 4 - Payment
    payment_type: 'המחאה - שיק',
    bank_name: 'לאומי',
    bank_branche_number: '123',
    bank_account_number: '123456',
    bank_account_name: testValue,
    credit_days: 'שוטף 30',
    check_delivery: 'סוכן',
    // Step 5 - Visits
    visit_days: 'א',
    visit_frequency: 'פעם בשבוע',
    visit_frequency_twice_a_week: '',
    visit_frequency_once_a_month: '',
    visit_like_customer: '',
    is_visit_limit: 'לא',
    visit_limit_hour_start: '',
    visit_limit_hour_end: '',
    // Step 6 - Signature
    signature: '',
    agent_name: '',
  }

  isTestMode.value = true
  currentStep.value = 5 // Go to signature step
  toast.info('נתוני בדיקה הוזנו - חתום ושלח')
}

// Validation functions
function validateStep1() {
  const fields = [
    { key: 'client_name', msg: 'עליך למלא שם לקוח/בית עסק' },
    { key: 'business_id', msg: 'עליך למלא ח.פ או ע.מ' },
    { key: 'city', msg: 'עליך לבחור עיר' },
    { key: 'street', msg: 'עליך לבחור רחוב' },
    { key: 'house_number', msg: 'עליך למלא מספר בית' },
    { key: 'contact_name', msg: 'עליך למלא שם איש קשר' },
  ]

  for (const field of fields) {
    if (!formData.value[field.key]) {
      toast.error(field.msg)
      return false
    }
  }

  if (!formData.value.mobile_phone || formData.value.mobile_phone.length < 10) {
    toast.error('עליך למלא טלפון נייד בצורה תקינה')
    return false
  }

  return true
}

function validateStep2() {
  const fields = [
    { key: 'owner_full_name', msg: 'עליך למלא שם מלא של בעל העסק' },
    { key: 'owner_birth_date', msg: 'עליך למלא תאריך לידה של בעל העסק' },
    { key: 'owner_address', msg: 'עליך למלא כתובת מגורים של בעל העסק' },
    { key: 'owner_id_photo', msg: 'עליך לצלם את התעודת זהות של בעל העסק' },
    { key: 'owner_chash_photo', msg: 'עליך לצלם את סרט הקופה של בעל העסק' },
    { key: 'owner_guaranty_photo', msg: 'עליך לצלם ערבות' },
    { key: 'owner_business_front_photo', msg: 'עליך לצלם את חזית העסק' },
  ]

  for (const field of fields) {
    if (!formData.value[field.key]) {
      toast.error(field.msg)
      return false
    }
  }

  if (!formData.value.owner_id || formData.value.owner_id.length < 6) {
    toast.error('עליך למלא מס זהות תקינה של בעל העסק')
    return false
  }

  return true
}

function validateStep3() {
  const fields = [
    { key: 'business_type', msg: 'עליך להזין את סוג העסק' },
    { key: 'back_check', msg: 'עליך להזין האם חזרו ללקוח צקים בעבר' },
    { key: 'kosher_type', msg: 'עליך להזין את רמת הכשרות של העסק' },
  ]

  for (const field of fields) {
    if (!formData.value[field.key]) {
      toast.error(field.msg)
      return false
    }
  }

  return true
}

function validateStep4() {
  if (!formData.value.payment_type) {
    toast.error('עליך לבחור תנאי תשלום')
    return false
  }

  if (formData.value.payment_type !== 'מזומן') {
    const bankFields = [
      { key: 'bank_name', msg: 'עליך לבחור את שם הבנק' },
      { key: 'bank_branche_number', msg: 'עליך להזין מספר סניף של הבנק' },
      { key: 'bank_account_number', msg: 'עליך להזין מספר חשבון של הבנק' },
      { key: 'bank_account_name', msg: 'עליך להזין את שם החשבון של הבנק' },
    ]

    for (const field of bankFields) {
      if (!formData.value[field.key]) {
        toast.error(field.msg)
        return false
      }
    }
  }

  if (!formData.value.credit_days) {
    toast.error('עליך להזין ימי אשראי')
    return false
  }

  if (!formData.value.check_delivery) {
    toast.error('עליך להזין למי תהייה מסירת הצק')
    return false
  }

  return true
}

function validateStep5() {
  if (!formData.value.visit_days) {
    toast.error('עליך להזין יום בשבוע לביקור סוכן')
    return false
  }

  if (!formData.value.visit_frequency) {
    toast.error('עליך להזין תדירות ביקורים')
    return false
  }

  if (
    formData.value.visit_frequency === 'פעם בשבועיים' &&
    !formData.value.visit_frequency_twice_a_week
  ) {
    toast.error('בחר את סוג הביקור')
    return false
  }

  if (
    formData.value.visit_frequency === 'פעם בחודש' &&
    !formData.value.visit_frequency_once_a_month
  ) {
    toast.error('בחר את סוג הביקור')
    return false
  }

  if (
    (formData.value.visit_frequency_twice_a_week === 'ידני' ||
      formData.value.visit_frequency_once_a_month === 'ידני') &&
    !formData.value.visit_type_manual
  ) {
    toast.error('עליך להזין את סוג הביקור הידני')
    return false
  }

  if (formData.value.visit_frequency === 'כמו לקוח' && !formData.value.visit_like_customer) {
    toast.error('רשום מספר לקוח')
    return false
  }

  if (!formData.value.is_visit_limit) {
    toast.error('עליך להזין האם שעות קבלת הסחורה מוגבלות')
    return false
  }

  if (formData.value.is_visit_limit === 'כן') {
    if (!formData.value.visit_limit_hour_start || !formData.value.visit_limit_hour_end) {
      toast.error('עליך להזין שעת התחלה ושעת סיום')
      return false
    }
  }

  return true
}

function validateStep6() {
  if (!signaturePadRef.value?.hasSignature()) {
    toast.error('עליך לחתום לפני אישור')
    return false
  }
  return true
}

// Navigation
function nextStep() {
  let valid = false

  switch (currentStep.value) {
    case 0:
      valid = validateStep1()
      break
    case 1:
      valid = validateStep2()
      break
    case 2:
      valid = validateStep3()
      break
    case 3:
      valid = validateStep4()
      break
    case 4:
      valid = validateStep5()
      break
    default:
      valid = true
  }

  if (valid) {
    currentStep.value++
  }
}

function prevStep() {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

// Submit
async function handleSubmit() {
  if (!validateStep6()) return

  isSubmitting.value = true

  try {
    // Upload signature
    const signatureBlob = await signaturePadRef.value.getBlob()
    if (signatureBlob) {
      signatureBlob.name = 'signature.png'
      const signatureUrl = await uploadImage(signatureBlob, uploadFolder.value, 'signature')
      formData.value.signature = signatureUrl
    }

    // Set metadata
    formData.value.createdAt = new Date()
    formData.value.agent_name = agentName.value
    formData.value.agent_number = agentNumber.value

    // Save to Firestore (Agent_portal/Agent_portal/New_client collection)
    const db = getFirebaseDb()
    const newClientRef = collection(db, 'Agent_portal', 'Agent_portal', 'New_client')
    await addDoc(newClientRef, { ...formData.value })

    // Send email notification via Firestore
    try {
      const emailHtml = generateNewClientEmailHtml(formData.value, {
        name: agentName.value,
        number: agentNumber.value,
      })
      const recipients = await getEmailRecipients('newClientAgent')
      if (recipients.length > 0) {
        await sendEmail({
          to: recipients,
          subject: `לקוח חדש: ${formData.value.client_name} - סוכן ${agentName.value}`,
          html: emailHtml,
          from: 'GoFood פורטל סוכנים',
        })
      } else {
        toast.warning('לא הוגדרו כתובות מייל לשליחה')
      }
    } catch (emailError) {
      console.error('Failed to queue email:', emailError)
      // Don't fail the whole operation if email fails
    }

    toast.success('הטופס נשלח בהצלחה!')

    // Navigate back to portal
    setTimeout(() => {
      router.push({ name: ROUTES.AGENTS_PORTAL })
    }, 1500)
  } catch (error) {
    console.error('Submit error:', error)
    toast.error('אירעה שגיאה בשליחת הטופס')
  } finally {
    isSubmitting.value = false
  }
}

function goBack() {
  router.push({ name: ROUTES.AGENTS_PORTAL })
}

// Handle window resize
function handleResize() {
  isMobile.value = window.innerWidth <= 600
}

onMounted(() => {
  handleResize()
  window.addEventListener('resize', handleResize)
  getCities()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <div class="create-client-view">
    <!-- Header -->
    <div class="view-header">
      <button class="back-btn" @click="goBack">
        <span class="material-symbols-rounded">arrow_forward</span>
      </button>
      <h1 class="view-title">יצירת לקוח חדש</h1>
      <!-- <button class="test-btn" @click="fillTestData">
        <span class="material-symbols-rounded">science</span>
        בדיקה
      </button> -->
    </div>

    <!-- Agent info (debug/verification) -->
    <div class="agent-info">
      <span class="agent-info-item"> <strong>שם הסוכן:</strong> {{ agentName || '—' }} </span>
      <span class="agent-info-item"> <strong>מספר סוכן:</strong> {{ agentNumber || '—' }} </span>
    </div>

    <!-- Stepper -->
    <FormStepper :steps="steps" :current-step="currentStep" />

    <!-- Form Content -->
    <div class="form-content">
      <!-- Step 1: Business Details -->
      <div v-show="currentStep === 0" class="step-content">
        <FormField
          v-model="formData.client_name"
          label="שם הלקוח/בית העסק"
          placeholder="שם מלא"
          required
        />
        <FormField
          v-model="formData.business_id"
          label="ח.פ או ע.מ"
          placeholder="ח.פ או ע.מ"
          required
        />
        <!-- City autocomplete -->
        <div class="field-group">
          <label class="field-label">עיר *</label>
          <div class="autocomplete-wrapper">
            <input
              v-model="citySearch"
              type="text"
              class="autocomplete-input"
              placeholder="הקלד לחיפוש עיר..."
              :disabled="loadingCities"
              @input="onCityInput"
              @keydown="onCityKeydown"
              @focus="showCityDropdown = true"
              @blur="onCityBlur"
            />
            <span v-if="loadingCities" class="autocomplete-loader">
              <span class="material-symbols-rounded spin">progress_activity</span>
            </span>
            <div v-if="showCityDropdown && filteredCities.length" class="autocomplete-dropdown">
              <div
                v-for="(city, idx) in filteredCities"
                :key="city"
                class="dropdown-item"
                :class="{ highlighted: idx === cityHighlightedIndex }"
                @mousedown.prevent="selectCity(city)"
              >
                {{ city }}
              </div>
            </div>
          </div>
        </div>

        <!-- Street autocomplete -->
        <div class="field-group">
          <label class="field-label">רחוב *</label>
          <div class="autocomplete-wrapper">
            <input
              v-model="streetSearch"
              type="text"
              class="autocomplete-input"
              placeholder="הקלד לחיפוש רחוב..."
              :disabled="!formData.city || loadingStreets"
              @input="onStreetInput"
              @keydown="onStreetKeydown"
              @focus="showStreetDropdown = true"
              @blur="onStreetBlur"
            />
            <span v-if="loadingStreets" class="autocomplete-loader">
              <span class="material-symbols-rounded spin">progress_activity</span>
            </span>
            <div v-if="showStreetDropdown && filteredStreets.length" class="autocomplete-dropdown">
              <div
                v-for="(street, idx) in filteredStreets"
                :key="street"
                class="dropdown-item"
                :class="{ highlighted: idx === streetHighlightedIndex }"
                @mousedown.prevent="selectStreet(street)"
              >
                {{ street }}
              </div>
            </div>
          </div>
        </div>

        <!-- House number -->
        <FormField
          v-model="formData.house_number"
          label="מספר בית"
          placeholder="מספר בית"
          required
        />
        <FormField v-model="formData.address_comments" label="הערות לכתובת" placeholder="הערות" />
        <FormField
          v-model="formData.business_phone"
          type="tel"
          label="מספר טלפון בבית העסק"
          placeholder="מספר טלפון"
        />
        <FormField
          v-model="formData.mobile_phone"
          type="tel"
          label="מספר טלפון נייד"
          placeholder="מספר נייד"
          required
        />
        <FormField
          v-model="formData.fax_number"
          type="tel"
          label="מספר פקס"
          placeholder="מספר פקס"
        />
        <FormField
          v-model="formData.contact_name"
          label="שם איש הקשר בעסק"
          placeholder="שם איש הקשר בעסק"
          required
        />

        <div class="step-actions">
          <AppButton variant="primary" @click="nextStep">המשך</AppButton>
        </div>
      </div>

      <!-- Step 2: Owner Details -->
      <div v-show="currentStep === 1" class="step-content">
        <FormField
          v-model="formData.owner_full_name"
          label="שם בעל העסק (פרטי + משפחה)"
          placeholder="שם מלא"
          required
        />
        <FormField
          v-model="formData.owner_id"
          label="מס תז של בעל העסק"
          placeholder="מס זהות של בעל העסק"
          required
        />
        <FormField
          v-model="formData.owner_birth_date"
          type="date"
          label="תאריך לידה של בעל העסק"
          required
        />
        <FormField
          v-model="formData.owner_address"
          label="כתובת מגורים של בעל העסק"
          placeholder="כתובת מלאה של המגורים של בעל העסק"
          required
        />

        <div class="images-grid">
          <ImageUpload
            v-model="formData.owner_id_photo"
            label="צילום ת״ז *"
            :folder="uploadFolder"
            file-name="id_photo"
          />
          <ImageUpload
            v-model="formData.owner_chash_photo"
            label="צילום סרט הקופה *"
            :folder="uploadFolder"
            file-name="cash_photo"
          />
          <ImageUpload
            v-model="formData.owner_guaranty_photo"
            label="צילום ערבות *"
            :folder="uploadFolder"
            file-name="guaranty_photo"
          />
          <ImageUpload
            v-model="formData.owner_business_front_photo"
            label="צילום חזית העסק *"
            :folder="uploadFolder"
            file-name="business_front_photo"
          />
        </div>

        <div class="step-actions step-actions-between">
          <AppButton variant="secondary" @click="prevStep">הקודם</AppButton>
          <AppButton variant="primary" @click="nextStep">המשך</AppButton>
        </div>
      </div>

      <!-- Step 3: Business Type -->
      <div v-show="currentStep === 2" class="step-content">
        <FormSelect
          v-model="formData.business_type"
          label="בחר את סוג העסק"
          :options="businessTypeOptions"
          placeholder="בחר את סוג העסק"
          required
        />

        <div class="field-group">
          <label class="field-label">וותק העסק בשנים</label>
          <input
            v-model.number="formData.business_seniority"
            type="number"
            min="0"
            max="50"
            class="number-input"
          />
        </div>

        <FormSelect
          v-model="formData.back_check"
          label="האם חזרו ללקוח צ'קים בעבר?"
          :options="yesNoOptions"
          placeholder="האם חזרו ללקוחות צ'קים בעבר?"
          required
        />

        <div class="field-group">
          <label class="field-label">הזן את מספר הקופות של הלקוח</label>
          <input
            v-model.number="formData.kupot_qnt"
            type="number"
            min="0"
            max="1000"
            class="number-input"
          />
        </div>

        <FormSelect
          v-model="formData.kosher_type"
          label="בחר את רמת הכשרות של העסק"
          :options="kosherTypeOptions"
          placeholder="רמת הכשרות של העסק"
          required
        />

        <div class="step-actions step-actions-between">
          <AppButton variant="secondary" @click="prevStep">הקודם</AppButton>
          <AppButton variant="primary" @click="nextStep">המשך</AppButton>
        </div>
      </div>

      <!-- Step 4: Payment Details -->
      <div v-show="currentStep === 3" class="step-content">
        <FormSelect
          v-model="formData.payment_type"
          label="בחר את תנאי התשלום"
          :options="paymentTypeOptions"
          placeholder="בחר את תנאי התשלום"
          required
        />

        <template v-if="showBankFields">
          <FormSelect
            v-model="formData.bank_name"
            label="בחר את שם הבנק"
            :options="bankNameOptions"
            placeholder="בחר את שם הבנק"
            required
          />
          <FormField
            v-model="formData.bank_branche_number"
            type="number"
            label="מספר סניף"
            placeholder="מספר סניף בנק"
            required
          />
          <FormField
            v-model="formData.bank_account_number"
            type="number"
            label="מספר חשבון"
            placeholder="מספר חשבון בנק"
            required
          />
          <FormField
            v-model="formData.bank_account_name"
            label="שם חשבון"
            placeholder="שם החשבון"
            required
          />
        </template>

        <FormField
          v-model="formData.credit_days"
          label="ימי אשראי"
          placeholder="נא לרשום בצורה הבאה: שוטף 60 או מזומן 30 וכדומה"
          required
        />
        <FormSelect
          v-model="formData.check_delivery"
          label="למי תהייה מסירת הצ'ק?"
          :options="checkDeliveryOptions"
          placeholder="נא לבחור למי ימסר הצ'ק"
          required
        />

        <div class="step-actions step-actions-between">
          <AppButton variant="secondary" @click="prevStep">הקודם</AppButton>
          <AppButton variant="primary" @click="nextStep">המשך</AppButton>
        </div>
      </div>

      <!-- Step 5: Visit Details -->
      <div v-show="currentStep === 4" class="step-content">
        <FormSelect
          v-model="formData.visit_days"
          label="יום בשבוע לביקור סוכן"
          :options="visitDaysOptions"
          placeholder="יום בשבוע לביקור סוכן"
          required
        />
        <FormSelect
          v-model="formData.visit_frequency"
          label="תדירות ביקורים"
          :options="visitFrequencyOptions"
          placeholder="תדירות ביקורים"
          required
        />

        <FormField
          v-if="showLikeCustomerField"
          v-model="formData.visit_like_customer"
          type="tel"
          label="תדירות ביקורים כמו לקוח מס:"
          placeholder="רשום את מספר הלקוח"
        />

        <FormSelect
          v-if="showTwiceWeekOption"
          v-model="formData.visit_frequency_twice_a_week"
          label="סוג הביקור"
          :options="visitFrequencyTwiceOptions"
          placeholder="בחר את סוג הביקור"
          required
        />

        <FormSelect
          v-if="showOnceMonthOption"
          v-model="formData.visit_frequency_once_a_month"
          label="סוג הביקור"
          :options="visitFrequencyOnceOptions"
          placeholder="בחר את סוג הביקור"
          required
        />

        <FormField
          v-if="showManualVisitType"
          v-model="formData.visit_type_manual"
          label="הזן סוג ביקור ידני"
          placeholder="רשום את סוג הביקור"
          required
        />

        <FormSelect
          v-model="formData.is_visit_limit"
          label="האם שעות קבלת הסחורה מוגבלות?"
          :options="yesNoOptions"
          placeholder="שעות קבלת הסחורה מוגבלות?"
          required
        />

        <div v-if="showVisitLimitHours" class="time-fields">
          <div class="time-field">
            <label class="field-label">משעה</label>
            <input v-model="formData.visit_limit_hour_start" type="time" class="time-input" />
          </div>
          <div class="time-field">
            <label class="field-label">עד שעה</label>
            <input v-model="formData.visit_limit_hour_end" type="time" class="time-input" />
          </div>
        </div>

        <div class="step-actions step-actions-between">
          <AppButton variant="secondary" @click="prevStep">הקודם</AppButton>
          <AppButton variant="primary" @click="nextStep">המשך</AppButton>
        </div>
      </div>

      <!-- Step 6: Signature -->
      <div v-show="currentStep === 5" class="step-content">
        <div class="signature-section">
          <p class="signature-text">אני הח"מ בעל העסק הנ"ל מאשר את כלל הפרטים האמורים לעיל</p>
          <SignaturePad
            ref="signaturePadRef"
            :width="isMobile ? 300 : 500"
            :height="isMobile ? 150 : 300"
          />
        </div>

        <div class="step-actions step-actions-between">
          <AppButton variant="secondary" @click="prevStep">הקודם</AppButton>
          <AppButton
            variant="primary"
            :loading="isSubmitting"
            :disabled="isSubmitting"
            @click="handleSubmit"
          >
            מאשר וסיים
          </AppButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.create-client-view {
  padding: var(--spacing-lg);
  max-width: 700px;
  margin: 0 auto;
}

.view-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
  color: var(--text-primary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.back-btn:hover {
  background: var(--bg-tertiary);
}

.view-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
  flex: 1;
}

.test-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px dashed var(--color-warning);
  background: rgba(245, 158, 11, 0.1);
  border-radius: var(--border-radius);
  color: var(--color-warning);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.test-btn:hover {
  background: rgba(245, 158, 11, 0.2);
}

.test-btn .material-symbols-rounded {
  font-size: 18px;
}

.form-content {
  margin-top: var(--spacing-lg);
}

.agent-info {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  margin-bottom: var(--spacing-md);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.agent-info-item strong {
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
  margin-inline-end: var(--spacing-xs);
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.step-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: var(--spacing-lg);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--border-color);
}

.step-actions-between {
  justify-content: space-between;
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
  margin: var(--spacing-md) 0;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.field-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.number-input,
.time-input {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-base);
  font-family: inherit;
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  transition: all var(--transition-fast);
}

.number-input:focus,
.time-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.time-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-md);
}

.time-field {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.signature-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  background: var(--bg-secondary);
  border-radius: var(--border-radius-lg);
}

.signature-text {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  text-align: center;
  margin: 0;
}

/* Autocomplete */
.autocomplete-wrapper {
  position: relative;
}

.autocomplete-input {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-base);
  font-family: inherit;
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  transition: all var(--transition-fast);
}

.autocomplete-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(30, 41, 59, 0.1);
}

.autocomplete-input:disabled {
  background: var(--bg-tertiary);
  color: var(--text-muted);
  cursor: not-allowed;
}

.autocomplete-loader {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
}

.spin {
  animation: spin 1s linear infinite;
  font-size: 20px;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.autocomplete-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-top: none;
  border-radius: 0 0 var(--border-radius) var(--border-radius);
  box-shadow: var(--shadow-md);
  z-index: 50;
}

.dropdown-item {
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.dropdown-item:hover,
.dropdown-item.highlighted {
  background: var(--bg-secondary);
}

/* Mobile */
@media (max-width: 600px) {
  .create-client-view {
    padding: var(--spacing-md);
    padding-bottom: calc(var(--spacing-md) + 80px);
  }

  .view-title {
    font-size: var(--font-size-lg);
  }

  .images-grid {
    grid-template-columns: 1fr;
  }

  .time-fields {
    grid-template-columns: 1fr;
  }

  .signature-section {
    padding: var(--spacing-md);
  }

  .step-actions {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: var(--spacing-md);
    background: var(--bg-primary);
    border-top: 1px solid var(--border-color);
    box-shadow: var(--shadow-lg);
    margin-top: 0;
    z-index: 100;
  }

  .step-actions :deep(.app-button) {
    flex: 1;
  }

  .step-actions-between {
    gap: var(--spacing-sm);
  }
}
</style>
